import Modal from '@/components/layout/Modal'
import { useShareLinks } from '@/hooks/useShareLinks'
import {
  CheckIcon,
  ClipboardDocumentIcon,
  LinkIcon,
} from '@heroicons/react/24/outline'
import { useCallback, useEffect, useState } from 'react'

import type {
  AssetSet,
  ShareLink,
  ShareLinkExpiryHours,
} from '@droidmap/asset-service-contract'
import {
  Badge,
  Button,
  Pagination,
  Spinner,
  ToggleGroup,
  ToggleGroupItem,
} from '@droidmap/frontend-ui'
import type { DroidMapMetadata } from '@droidmap/shared-service-contract'

const EXPIRY_PRESETS: { hours: ShareLinkExpiryHours; label: string }[] = [
  { hours: 24, label: '24 hours' },
  { hours: 168, label: '7 days' },
  { hours: 720, label: '30 days' },
  { hours: 2160, label: '90 days' },
]

const formatDateTime = (d: Date | string): string =>
  new Date(d).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

const linkStatus = (link: ShareLink): 'active' | 'expired' | 'revoked' => {
  if (link.revokedAt) return 'revoked'
  if (new Date(link.expiresAt).getTime() <= Date.now()) return 'expired'
  return 'active'
}

// Locale-grouped so a heavily-shared link ("12,431 downloads") stays readable.
const formatDownloads = (count: number): string =>
  count === 0
    ? 'Not downloaded yet'
    : `${count.toLocaleString()} ${count === 1 ? 'download' : 'downloads'}`

export interface ShareAssetSetDialogProps {
  assetSet: AssetSet
  isOpen: boolean
  onClose: () => void
  /**
   * Route metadata carrying the active projectId. Required — the share-link
   * API is project-scoped (the backend checks UPDATE_ASSET_SET on the
   * project), so the client must send the `x-droidmap-project` header.
   */
  metadata: DroidMapMetadata
}

/**
 * Share dialog for an asset set (#64): create a public download link with an
 * expiry preset, copy the one-time URL, and list/revoke existing links. The
 * raw link token is only available in the create response — once this dialog
 * closes (or another link is created) it cannot be recovered, only revoked.
 */
export function ShareAssetSetDialog({
  assetSet,
  isOpen,
  onClose,
  metadata,
}: ShareAssetSetDialogProps) {
  const {
    shareLinks,
    isLoading,
    page,
    setPage,
    totalCount,
    pageCount,
    createShareLink,
    isCreating,
    revokeShareLink,
    isRevoking,
    error,
  } = useShareLinks(assetSet.id, metadata)

  const [expiresInHours, setExpiresInHours] =
    useState<ShareLinkExpiryHours>(168)
  const [createdUrl, setCreatedUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [revokingId, setRevokingId] = useState<string | null>(null)

  // A freshly created URL is one-time information — drop it when the dialog
  // closes so a later reopen doesn't imply it can be recovered. Done during
  // render: in an effect the URL survived one render past the close, which is
  // exactly the window this is meant to shut.
  const [wasOpen, setWasOpen] = useState(isOpen)
  if (wasOpen !== isOpen) {
    setWasOpen(isOpen)
    if (!isOpen) {
      setCreatedUrl(null)
      setCopied(false)
    }
  }

  // Revoking the last link on the last page leaves this page empty (its rows
  // shifted onto earlier pages). Step back so the user isn't stranded on a
  // blank trailing page.
  useEffect(() => {
    if (!isLoading && shareLinks.length === 0 && page > 0) {
      setPage(page - 1)
    }
  }, [isLoading, shareLinks.length, page, setPage])

  const handleCreate = useCallback(async () => {
    const result = await createShareLink(expiresInHours)
    setCreatedUrl(`${window.location.origin}/share/${result.token}`)
    setCopied(false)
  }, [createShareLink, expiresInHours])

  const handleCopy = useCallback(async () => {
    if (!createdUrl) return
    await navigator.clipboard.writeText(createdUrl)
    setCopied(true)
  }, [createdUrl])

  const handleRevoke = useCallback(
    async (shareLinkId: string) => {
      setRevokingId(shareLinkId)
      try {
        await revokeShareLink(shareLinkId)
      } finally {
        setRevokingId(null)
      }
    },
    [revokeShareLink],
  )

  return (
    <Modal
      title="Share asset set"
      description={
        <>
          Anyone with the link can download{' '}
          <span className="text-text font-medium">{assetSet.name}</span> as a
          ZIP until the link expires or is revoked.
        </>
      }
      icon={<LinkIcon />}
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="sm:max-w-xl"
    >
      <div className="space-y-5">
        {/* Create */}
        <div className="border-border bg-bg-2 rounded-md border p-4">
          <div className="text-text-dim mb-2 text-sm font-medium">
            Link expires after
          </div>
          <ToggleGroup
            type="single"
            value={String(expiresInHours)}
            onValueChange={(value) => {
              // Radix fires '' when the active item is re-clicked (deselect).
              // Expiry is required, so ignore the empty value and keep the
              // current selection.
              if (value) {
                setExpiresInHours(Number(value) as ShareLinkExpiryHours)
              }
            }}
            aria-label="Link expiry"
            className="flex-wrap"
          >
            {EXPIRY_PRESETS.map((preset) => (
              <ToggleGroupItem key={preset.hours} value={String(preset.hours)}>
                {preset.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
          <div className="mt-4 flex justify-end">
            <Button onClick={handleCreate} isLoading={isCreating}>
              Create share link
            </Button>
          </div>
        </div>

        {/* One-time URL reveal */}
        {createdUrl && (
          <div className="border-accent-tint bg-accent-soft/30 rounded-md border p-4">
            <div className="text-text mb-2 text-sm font-medium">
              Share link created
            </div>
            <div className="flex items-center gap-2">
              <code
                data-testid="created-share-url"
                className="border-border bg-bg-2 text-text-dim min-w-0 flex-1 truncate rounded-md border px-3 py-2 font-mono text-xs"
              >
                {createdUrl}
              </code>
              <Button onClick={handleCopy} variant="outline" size="sm">
                {copied ? (
                  <>
                    <CheckIcon className="mr-1 h-4 w-4" />
                    Copied
                  </>
                ) : (
                  <>
                    <ClipboardDocumentIcon className="mr-1 h-4 w-4" />
                    Copy
                  </>
                )}
              </Button>
            </div>
            <div className="text-text-mute mt-2 text-xs">
              Copy it now — for security this link won&apos;t be shown again.
            </div>
          </div>
        )}

        {error && (
          <div className="text-danger text-sm">
            {error.message || 'Something went wrong. Please try again.'}
          </div>
        )}

        {/* Existing links */}
        <div>
          <div className="text-text-dim mb-2 text-sm font-medium">
            Existing links
            {totalCount > 0 && (
              <span className="text-text-mute ml-1 font-normal">
                ({totalCount.toLocaleString()})
              </span>
            )}
          </div>
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Spinner />
            </div>
          ) : shareLinks.length === 0 ? (
            <div className="text-text-mute border-border rounded-md border border-dashed px-4 py-6 text-center text-sm">
              No share links yet.
            </div>
          ) : (
            <ul className="border-border divide-border max-h-72 divide-y overflow-y-auto rounded-md border">
              {shareLinks.map((link) => {
                const status = linkStatus(link)
                return (
                  <li
                    key={link.id}
                    className="flex items-center justify-between gap-3 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <div className="text-text-dim truncate font-mono text-xs">
                        Created {formatDateTime(link.created)}
                      </div>
                      <div className="text-text-mute mt-0.5 text-xs">
                        {status === 'active'
                          ? `Expires ${formatDateTime(link.expiresAt)}`
                          : status === 'expired'
                            ? `Expired ${formatDateTime(link.expiresAt)}`
                            : `Revoked ${formatDateTime(link.revokedAt as Date)}`}
                      </div>
                      <div className="text-text-mute mt-0.5 text-xs">
                        {formatDownloads(link.downloadCount ?? 0)}
                        {link.lastDownloadedAt &&
                          ` · Last ${formatDateTime(link.lastDownloadedAt)}`}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Badge
                        size="sm"
                        tone={status === 'active' ? 'success' : 'neutral'}
                      >
                        {status}
                      </Badge>
                      {status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRevoke(link.id)}
                          isLoading={isRevoking && revokingId === link.id}
                        >
                          Revoke
                        </Button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
          {pageCount > 1 && (
            <div className="mt-3 flex justify-center">
              <Pagination
                currentPage={page + 1}
                maxPage={pageCount}
                hasMoreBeyondKnownPages={false}
                onPageChange={(next) => setPage(next - 1)}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default ShareAssetSetDialog
